import React, { useState } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramFlowHoodKFactor } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.flowHoodKFactor.v1';

type Fields = { kFactor: string; deltaP: string; flow: string };
const EMPTY: Fields = { kFactor: '', deltaP: '', flow: '' };

/**
 * Flow Hood K-Factor — Q = K × √(ΔP) (or solve for K / ΔP).
 * ΔP in pressure units; Q in flow units. K is dimensionless (Imperial-referenced).
 * Note: K-factor is equipment-specific; in SI the formula constant changes.
 * We keep internal math in Imperial (K is Imperial-defined).
 */
const AirflowFlowHoodKFactorScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp } = useUnits();

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const calculate = () => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v.trim() !== '').length;
    if (filled < 2) {
      setError('Fill any 2 of the 3 fields to solve for the missing one.');
      return;
    }
    if (filled === 3) {
      setError('All 3 values are filled — clear one to solve for it.');
      return;
    }

    if (fields.flow.trim() === '') {
      const k = Number(fields.kFactor);
      const dp = toImp(fields.deltaP, 'pressure');
      if (!Number.isFinite(k) || k <= 0) {
        setError('K-factor must be a number greater than zero.');
        return;
      }
      if (!Number.isFinite(dp) || dp < 0) {
        setError('ΔP must be a number (0 or greater).');
        return;
      }
      setSolvedFor('flow');
      set({ flow: fromImp(k * Math.sqrt(dp), 'flow', 1) });
      return;
    }

    if (fields.kFactor.trim() === '') {
      const q = toImp(fields.flow, 'flow');
      const dp = toImp(fields.deltaP, 'pressure');
      if (!Number.isFinite(q) || q < 0) {
        setError('Flow must be a number (0 or greater).');
        return;
      }
      if (!Number.isFinite(dp) || dp <= 0) {
        setError('ΔP must be a number greater than zero to solve for K.');
        return;
      }
      setSolvedFor('kFactor');
      set({ kFactor: (q / Math.sqrt(dp)).toFixed(4) });
      return;
    }

    if (fields.deltaP.trim() === '') {
      const q = toImp(fields.flow, 'flow');
      const k = Number(fields.kFactor);
      if (!Number.isFinite(q) || q < 0) {
        setError('Flow must be a number (0 or greater).');
        return;
      }
      if (!Number.isFinite(k) || k <= 0) {
        setError('K-factor must be a number greater than zero.');
        return;
      }
      setSolvedFor('deltaP');
      set({ deltaP: fromImp(Math.pow(q / k, 2), 'pressure', 5) });
      return;
    }
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  const result = [
    fields.kFactor && { label: 'K-Factor', value: fields.kFactor, emphasis: solvedFor === 'kFactor' },
    fields.deltaP && { label: 'ΔP', value: fields.deltaP, unit: unit('pressure'), emphasis: solvedFor === 'deltaP' },
    fields.flow && { label: 'Flow', value: fields.flow, unit: unit('flow'), emphasis: solvedFor === 'flow' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="Flow Hood K-Factor"
      subtitle="Calculate flow from hood K-factor and differential pressure (or solve for K)"
      formula={
        <>
          <FormulaLine>Q = K × √(ΔP)</FormulaLine>
          <FormulaNote>
            Q = flow ({unit('flow')}), K = hood factor, ΔP = differential pressure ({unit('pressure')})
          </FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: Q = K × √(ΔP)"
      diagram={<DiagramFlowHoodKFactor />}
      outputSlots={{
        k_factor: fields.kFactor ? parseFloat(fields.kFactor) : undefined,
        velocity_pressure_iwg: fields.deltaP ? toImp(fields.deltaP, 'pressure') : undefined,
        cfm: fields.flow ? toImp(fields.flow, 'flow') : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.k_factor != null && !fields.kFactor) {
          patch.kFactor = values.k_factor.toFixed(4);
        }
        if (values.velocity_pressure_iwg != null && !fields.deltaP) {
          patch.deltaP = fromImp(values.velocity_pressure_iwg, 'pressure', 4);
        }
        if (values.cfm != null && !fields.flow) {
          patch.flow = fromImp(values.cfm, 'flow', 1);
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="K-Factor"
          value={fields.kFactor}
          onChangeText={(v) => set({ kFactor: v })}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`ΔP (${unit('pressure')})`}
          value={fields.deltaP}
          onChangeText={(v) => set({ deltaP: v })}
          rightText={unit('pressure')}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Flow (${unit('flow')})`}
          value={fields.flow}
          onChangeText={(v) => set({ flow: v })}
          rightText={unit('flow')}
          onSubmitEditing={calculate}
        />
      </View>
    </CalcShell>
  );
};

export default AirflowFlowHoodKFactorScreen;
